/* eslint-disable mobx/missing-observer */
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { notification } from 'antd';
import { union } from 'lodash-es';

import { TWagon, TWagonSeat } from 'app/railways/domains/trips/core/trips';

type WagonDataProviderProps = {
  direction: 'forward' | 'backward';
  scheme: {
    seats: { order: number[]; record: Record<number, { num: number }> };
  };
  seats: TWagon['seats'];
  value: number[];
  disabled: boolean;
  children: ReactNode;
  seatExtraDataAttrsBuilder?: (
    seat: TWagonSeat,
  ) => Record<`data-${string}`, string | number | boolean | undefined | null>;
  onChange?: (seats: number[]) => void;
};

type CtxType = {
  direction: 'forward' | 'backward';
  disabled: boolean;
  seatsMap: Map<number, TWagonSeat>;
  selectSeatsSet: Set<number>;
  onSeatClick: (data: { withRange: boolean; seat: number }) => void;
};

export const Context = createContext<CtxType | null>(null);

export const useWagonData = () => useContext(Context);

export const WagonDataProvider = ({
  direction,
  seats,
  scheme,
  seatExtraDataAttrsBuilder,
  disabled = false,
  value,
  onChange,
  children,
}: WagonDataProviderProps) => {
  const selectSeatsSet = useMemo(() => new Set<number>(value), [value]);

  const seatsMap = useMemo(() => {
    const sMap = new Map<number, TWagonSeat>();
    seats.forEach((seat) => {
      sMap.set(seat.seatNum, {
        ...seat,
        extraDataAttrs: seatExtraDataAttrsBuilder?.(seat),
      });
    });

    return sMap;
  }, [seatExtraDataAttrsBuilder, seats]);

  const onSeatClick = ({
    withRange,
    seat,
  }: {
    withRange: boolean;
    seat: number;
  }) => {
    if (withRange) {
      const firstSeatInRange = value.at(-1);
      const lastSeatInRange = seat;

      if (!firstSeatInRange) {
        notification.open({
          message: 'Выбор диапазона мест',
          type: 'info',
          duration: null,
          description:
            'Вы кликнули по месту с зажатой клавишей Shift. Для выбора диапазона мест сначала кликните на первое место, затем на последнее с уже зажатой клавишей Shift.',
        });
        return;
      }

      const firstInRangeIndex = scheme.seats.order.indexOf(firstSeatInRange);
      const lastInRangeIndex = scheme.seats.order.indexOf(lastSeatInRange);

      if (firstInRangeIndex === -1 || lastInRangeIndex === -1) {
        notification.open({
          message: 'Выбор диапазона мест',
          type: 'error',
          duration: null,
          description:
            'Не найдено выбранное место в данных схемы вагона. Возмозно ошибка, обратитесь к разработчику',
        });
      }

      const selectedSeats = [];

      if (firstInRangeIndex > lastInRangeIndex) {
        for (let i = firstInRangeIndex; i >= lastInRangeIndex; i--) {
          const seatNum = scheme.seats.order[i];
          selectedSeats.push(seatNum);
        }
      }

      if (firstInRangeIndex < lastInRangeIndex) {
        for (let i = firstInRangeIndex; i <= lastInRangeIndex; i++) {
          const seatNum = scheme.seats.order[i];
          selectedSeats.push(seatNum);
        }
      }

      const result = union(value, selectedSeats);
      onChange?.(result);

      return;
    }

    const newSelectedSeats = value.includes(seat)
      ? value.filter((s) => s !== seat)
      : [...value, seat];
    onChange?.(newSelectedSeats);
  };

  return (
    <Context.Provider
      value={{ direction, seatsMap, disabled, selectSeatsSet, onSeatClick }}
    >
      {children}
    </Context.Provider>
  );
};
